import { createAsyncThunk } from "@reduxjs/toolkit"
import { AppId, AppThunkAPIType } from "../../../app/appTypes"
import { deleteWithAuth, getWithAuth, postWithAuth } from "../../../http"
import { Project, ProjectHeader } from "../projectSlice"
import { ProcessTeamMember } from "../validationProcess/validationProcessApi"

export interface ValidationProjectTeamState {
    manager: string | null
    members: ProcessTeamMember[]
}

export interface CreateValidationProjectWizardPayload {
    type: number
    no: string
    name: string
    plannedStart: string | null
    plannedEnd: string | null
    executionType: number
    team: ValidationProjectTeamState
}

export interface ValidationProjectDocumentDto {
    id: number
    documentId: number
    no: string
    title: string
    type: string
    status: string
    order: number
}

export interface ValidationProcessReferenceDto {
    id: number
    no: string
}

export interface ValidationProjectDeviceDto {
    id: number
    deviceId: number
    name: string
    serialNo: string
    equipmentNo: string
    status: string
    isValidationProcessInProgress: boolean
    validationProcess: ValidationProcessReferenceDto | null
    order: number
    description: string
}

export interface ValidationProjectComputerizedSystemDto {
    id: number
    computerizedSystemId: number
    name: string
    status: string | null
    isValidationProcessInProgress: boolean
    validationProcess: ValidationProcessReferenceDto | null
    order: number
    description: string
}

export interface ValidationProjectProcessDto {
    id: number
    guid: string
    no: string
    name: string
    status: string
    startDate: Date | string | null
    finishDate: Date | string | null
}

export interface LinkProjectDocumentPayload {
    documentId: number
    order: number | null
}

export interface LinkProjectDevicePayload {
    deviceId: number
    order: number | null
    description: string
}

export interface LinkProjectComputerizedSystemPayload {
    computerizedSystemId: number
    order: number | null
}

export interface LinkProjectProcessPayload {
    processId: number
}


export const loadValidationProjects = createAsyncThunk<ProjectHeader[], { filter?: string }, AppThunkAPIType>(
    "projects/load", async ({ filter }, { dispatch, rejectWithValue }) => {
        const f = filter !== "" ? `&${filter}` : filter
        const result = await dispatch(getWithAuth({ url: `api/validation-projects?${f}` }))
        const { payload } = result

        if (getWithAuth.fulfilled.match(result)) {
            return payload as ProjectHeader[]
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    })

export const loadValidationProject = createAsyncThunk<Project, AppId, AppThunkAPIType>(
    "projects/loadOne", async (id: AppId, { dispatch, rejectWithValue }) => {
        const result = await dispatch(getWithAuth({ url: `api/validation-projects/${id}` }))
        const { payload } = result
        if (getWithAuth.fulfilled.match(result)) {
            return payload as Project
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    })

export const loadValidationProjectDocuments = createAsyncThunk<ValidationProjectDocumentDto[], AppId, AppThunkAPIType>(
    "validation-projects/load-documents",
    async (id, { dispatch, rejectWithValue }) => {
        const result = await dispatch(getWithAuth({ url: `api/validation-projects/${id}/documents` }))
        const { payload } = result

        if (getWithAuth.fulfilled.match(result)) {
            return payload as ValidationProjectDocumentDto[]
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    }
)

export const loadValidationProjectDevices = createAsyncThunk<ValidationProjectDeviceDto[], AppId, AppThunkAPIType>(
    "validation-projects/load-devices",
    async (id, { dispatch, rejectWithValue }) => {
        const result = await dispatch(getWithAuth({ url: `api/validation-projects/${id}/devices` }))
        const { payload } = result

        if (getWithAuth.fulfilled.match(result)) {
            return payload as ValidationProjectDeviceDto[]
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    }
)

export const loadValidationProjectProcesses = createAsyncThunk<ValidationProjectProcessDto[], AppId, AppThunkAPIType>(
    "validation-projects/load-processes",
    async (id, { dispatch, rejectWithValue }) => {
        const result = await dispatch(getWithAuth({ url: `api/validation-projects/${id}/processes` }))
        const { payload } = result

        if (getWithAuth.fulfilled.match(result)) {
            return payload as ValidationProjectProcessDto[]
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    }
)

export const loadValidationProjectComputerizedSystems = createAsyncThunk<ValidationProjectComputerizedSystemDto[], AppId, AppThunkAPIType>(
    "validation-projects/load-computerized-systems",
    async (id, { dispatch, rejectWithValue }) => {
        const result = await dispatch(getWithAuth({ url: `api/validation-projects/${id}/computerized-systems` }))
        const { payload } = result

        if (getWithAuth.fulfilled.match(result)) {
            return payload as ValidationProjectComputerizedSystemDto[]
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    }
)

export const linkValidationProjectDocument = createAsyncThunk<ValidationProjectDocumentDto, { id: AppId, payload: LinkProjectDocumentPayload }, AppThunkAPIType>(
    "validation-projects/link-document",
    async ({ id, payload }, { dispatch, rejectWithValue }) => {
        const result = await dispatch(postWithAuth({
            url: `api/validation-projects/${id}/documents`,
            payload,
        }))
        const { payload: resultPayload } = result

        if (postWithAuth.fulfilled.match(result)) {
            return resultPayload as ValidationProjectDocumentDto
        } else {
            return rejectWithValue(resultPayload ?? { kind: 'unknown' })
        }
    }
)

export const linkValidationProjectDevice = createAsyncThunk<ValidationProjectDeviceDto, { id: AppId, payload: LinkProjectDevicePayload }, AppThunkAPIType>(
    "validation-projects/link-device",
    async ({ id, payload }, { dispatch, rejectWithValue }) => {
        const result = await dispatch(postWithAuth({
            url: `api/validation-projects/${id}/devices`,
            payload,
        }))
        const { payload: resultPayload } = result

        if (postWithAuth.fulfilled.match(result)) {
            return resultPayload as ValidationProjectDeviceDto
        } else {
            return rejectWithValue(resultPayload ?? { kind: 'unknown' })
        }
    }
)

export const linkValidationProjectComputerizedSystem = createAsyncThunk<ValidationProjectComputerizedSystemDto, { id: AppId, payload: LinkProjectComputerizedSystemPayload }, AppThunkAPIType>(
    "validation-projects/link-computerized-system",
    async ({ id, payload }, { dispatch, rejectWithValue }) => {
        const result = await dispatch(postWithAuth({
            url: `api/validation-projects/${id}/computerized-systems`,
            payload,
        }))
        const { payload: resultPayload } = result

        if (postWithAuth.fulfilled.match(result)) {
            return resultPayload as ValidationProjectComputerizedSystemDto
        } else {
            return rejectWithValue(resultPayload ?? { kind: 'unknown' })
        }
    }
)

export const linkValidationProjectProcess = createAsyncThunk<ValidationProjectProcessDto, { id: AppId, payload: LinkProjectProcessPayload }, AppThunkAPIType>(
    "validation-projects/link-process",
    async ({ id, payload }, { dispatch, rejectWithValue }) => {
        const result = await dispatch(postWithAuth({
            url: `api/validation-projects/${id}/processes`,
            payload,
        }))
        const { payload: resultPayload } = result

        if (postWithAuth.fulfilled.match(result)) {
            return resultPayload as ValidationProjectProcessDto
        } else {
            return rejectWithValue(resultPayload ?? { kind: 'unknown' })
        }
    }
)

export const unlinkValidationProjectDevice = createAsyncThunk<AppId, { projectId: AppId, deviceId: AppId }, AppThunkAPIType>(
    "validation-projects/unlink-device",
    async ({ projectId, deviceId }, { dispatch, rejectWithValue }) => {
        const result = await dispatch(deleteWithAuth({ url: `api/validation-projects/${projectId}/devices/${deviceId}` }))
        const { payload } = result

        if (deleteWithAuth.fulfilled.match(result)) {
            return deviceId
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    }
)

export const unlinkValidationProjectComputerizedSystem = createAsyncThunk<AppId, { projectId: AppId, computerizedSystemId: AppId }, AppThunkAPIType>(
    "validation-projects/unlink-computerized-system",
    async ({ projectId, computerizedSystemId }, { dispatch, rejectWithValue }) => {
        const result = await dispatch(deleteWithAuth({ url: `api/validation-projects/${projectId}/computerized-systems/${computerizedSystemId}` }))
        const { payload } = result

        if (deleteWithAuth.fulfilled.match(result)) {
            return computerizedSystemId
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    }
)

export const unlinkValidationProjectProcess = createAsyncThunk<AppId, { projectId: AppId, processId: AppId }, AppThunkAPIType>(
    "validation-projects/unlink-process",
    async ({ projectId, processId }, { dispatch, rejectWithValue }) => {
        const result = await dispatch(deleteWithAuth({ url: `api/validation-projects/${projectId}/processes/${processId}` }))
        const { payload } = result

        if (deleteWithAuth.fulfilled.match(result)) {
            return processId
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    }
)

export const createValidationProject = createAsyncThunk<ProjectHeader, CreateValidationProjectWizardPayload, AppThunkAPIType>(
    "validation-projects/create",
    async (createPayload, { dispatch, rejectWithValue }) => {
        const result = await dispatch(postWithAuth({
            url: "api/validation-projects",
            payload: createPayload,
        }))
        const { payload } = result

        if (postWithAuth.fulfilled.match(result)) {
            return payload as ProjectHeader
        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    }
)

